import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { formatVndIntegerInput, formatVndIntegerValue, parseVndIntegerInput } from "utils";
import { PayrollDistributionScheduleService } from "services/payroll-distribution-schedule";
import { getPrintTemplate } from "./components/printTemplate";

const DEFAULT_DEBIT_ROWS = [
  { serialNumber: "1", debitObject: "TK 622 - Chi phí nhân công trực tiếp\n- Phân xưởng (sản phẩm)\n- ...............\n- Phân xưởng (sản phẩm)\n- ..............." },
  { serialNumber: "2", debitObject: "TK 623 - Chi phí sử dụng máy thi công\n- ..............." },
  { serialNumber: "3", debitObject: "TK 627 - Chi phí sản xuất chung\n- Phân xưởng (sản phẩm)\n- ...............\n- Phân xưởng (sản phẩm)\n- ..............." },
  { serialNumber: "4", debitObject: "TK 641 - Chi phí bán hàng" },
  { serialNumber: "5", debitObject: "TK 642 - Chi phí quản lý doanh nghiệp" },
  { serialNumber: "6", debitObject: "TK 242 - Chi phí chờ phân bổ" },
  { serialNumber: "7", debitObject: "TK 335 - Chi phí phải trả" },
  { serialNumber: "8", debitObject: "TK 241 - Xây dựng cơ bản dở dang" },
  { serialNumber: "9", debitObject: "TK 334 - Phải trả người lao động" },
  { serialNumber: "10", debitObject: "TK 338 - Phải trả, phải nộp khác" },
  { serialNumber: "11", debitObject: "................" },
];

const numberColumns = [
  "wage",
  "otherAmount",
  "tk334Total",
  "unionFee",
  "socialInsurance",
  "healthInsurance",
  "unemploymentInsurance",
  "tk338Total",
  "tk335Expense",
];

const createRow = (row = {}) => ({
  serialNumber: row.serialNumber || "",
  debitObject: row.debitObject || "",
  wage: "",
  otherAmount: "",
  tk334Total: "",
  unionFee: "",
  socialInsurance: "",
  healthInsurance: "",
  unemploymentInsurance: "",
  tk338Total: "",
  tk335Expense: "",
  grandTotal: "",
});

const createDefaultRows = () => DEFAULT_DEBIT_ROWS.map((item) => createRow(item));

const parseMoney = (value) => {
  const amount = parseVndIntegerInput(value);
  return Number.isFinite(amount) ? Math.round(amount) : 0;
};

const computeRow = (row) => {
  const wage = parseMoney(row.wage);
  const otherAmount = parseMoney(row.otherAmount);
  const tk334Total = parseMoney(row.tk334Total) || wage + otherAmount;

  const unionFee = parseMoney(row.unionFee);
  const socialInsurance = parseMoney(row.socialInsurance);
  const healthInsurance = parseMoney(row.healthInsurance);
  const unemploymentInsurance = parseMoney(row.unemploymentInsurance);
  const tk338Total = parseMoney(row.tk338Total) || unionFee + socialInsurance + healthInsurance + unemploymentInsurance;

  const tk335Expense = parseMoney(row.tk335Expense);
  const grandTotal = tk334Total + tk338Total + tk335Expense;

  return {
    ...row,
    wage,
    otherAmount,
    tk334Total,
    unionFee,
    socialInsurance,
    healthInsurance,
    unemploymentInsurance,
    tk338Total,
    tk335Expense,
    grandTotal,
  };
};

export default function PayrollDistributionScheduleDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [header, setHeader] = useState({
    unit: "",
    department: "",
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
    documentNumber: "",
  });
  const [rows, setRows] = useState(createDefaultRows());

  useEffect(() => {
    if (!id) return;

    (async () => {
      try {
        const response = await PayrollDistributionScheduleService.getById(id);
        const data = response.data ? response.data : response;

        setHeader({
          unit: data.unit || "",
          department: data.department || "",
          month: String(data.month || "").padStart(2, "0"),
          year: String(data.year || ""),
          documentNumber: data.documentNumber || "",
        });

        const mappedRows = (data.rows || []).map((row) => ({
          serialNumber: row.serialNumber || "",
          debitObject: row.debitObject || "",
          wage: formatVndIntegerInput(row.wage),
          otherAmount: formatVndIntegerInput(row.otherAmount),
          tk334Total: formatVndIntegerInput(row.tk334Total),
          unionFee: formatVndIntegerInput(row.unionFee),
          socialInsurance: formatVndIntegerInput(row.socialInsurance),
          healthInsurance: formatVndIntegerInput(row.healthInsurance),
          unemploymentInsurance: formatVndIntegerInput(row.unemploymentInsurance),
          tk338Total: formatVndIntegerInput(row.tk338Total),
          tk335Expense: formatVndIntegerInput(row.tk335Expense),
          grandTotal: formatVndIntegerInput(row.grandTotal),
        }));

        setRows(mappedRows.length ? mappedRows : createDefaultRows());
      } catch (error) {
        toast.error("Không thể tải dữ liệu bảng phân bổ tiền lương");
      }
    })();
  }, [id]);

  const calculatedRows = useMemo(() => rows.map((row) => computeRow(row)), [rows]);

  const totals = useMemo(() => {
    return calculatedRows.reduce(
      (acc, row) => ({
        wage: acc.wage + row.wage,
        otherAmount: acc.otherAmount + row.otherAmount,
        tk334Total: acc.tk334Total + row.tk334Total,
        unionFee: acc.unionFee + row.unionFee,
        socialInsurance: acc.socialInsurance + row.socialInsurance,
        healthInsurance: acc.healthInsurance + row.healthInsurance,
        unemploymentInsurance: acc.unemploymentInsurance + row.unemploymentInsurance,
        tk338Total: acc.tk338Total + row.tk338Total,
        tk335Expense: acc.tk335Expense + row.tk335Expense,
        grandTotal: acc.grandTotal + row.grandTotal,
      }),
      {
        wage: 0,
        otherAmount: 0,
        tk334Total: 0,
        unionFee: 0,
        socialInsurance: 0,
        healthInsurance: 0,
        unemploymentInsurance: 0,
        tk338Total: 0,
        tk335Expense: 0,
        grandTotal: 0,
      }
    );
  }, [calculatedRows]);

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setRowField = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      if (numberColumns.includes(field)) {
        next[index] = {
          ...next[index],
          [field]: formatVndIntegerInput(value),
        };
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, createRow({ serialNumber: String(prev.length + 1), debitObject: "" })]);
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const buildPayload = () => {
    const unit = String(header.unit || "").trim();
    const department = String(header.department || "").trim();
    const month = String(header.month || "").trim().padStart(2, "0");
    const year = String(header.year || "").trim();
    const documentNumber = String(header.documentNumber || "").trim();

    if (!unit) throw new Error("Đơn vị không được để trống");
    if (!department) throw new Error("Bộ phận không được để trống");
    if (!/^\d{1,2}$/.test(month) || Number(month) < 1 || Number(month) > 12) {
      throw new Error("Tháng không hợp lệ (1-12)");
    }
    if (!/^\d{4}$/.test(year)) {
      throw new Error("Năm phải gồm 4 chữ số");
    }
    if (!documentNumber) throw new Error("Số không được để trống");

    const normalizedRows = calculatedRows
      .map((row) => ({
        serialNumber: String(row.serialNumber || "").trim(),
        debitObject: String(row.debitObject || "").trim(),
        wage: row.wage,
        otherAmount: row.otherAmount,
        tk334Total: row.tk334Total,
        unionFee: row.unionFee,
        socialInsurance: row.socialInsurance,
        healthInsurance: row.healthInsurance,
        unemploymentInsurance: row.unemploymentInsurance,
        tk338Total: row.tk338Total,
        tk335Expense: row.tk335Expense,
        grandTotal: row.grandTotal,
      }))
      .filter((row) => {
        const hasMoney =
          row.wage > 0 ||
          row.otherAmount > 0 ||
          row.tk334Total > 0 ||
          row.unionFee > 0 ||
          row.socialInsurance > 0 ||
          row.healthInsurance > 0 ||
          row.unemploymentInsurance > 0 ||
          row.tk338Total > 0 ||
          row.tk335Expense > 0 ||
          row.grandTotal > 0;
        return row.serialNumber || row.debitObject || hasMoney;
      });

    if (!normalizedRows.length) {
      throw new Error("Bảng phân bổ phải có ít nhất 1 dòng dữ liệu");
    }

    normalizedRows.forEach((row, index) => {
      if (!row.serialNumber) {
        throw new Error(`Dòng ${index + 1}: Số TT không được để trống`);
      }
    });

    return {
      unit,
      department,
      month,
      year,
      documentNumber,
      rows: normalizedRows,
    };
  };

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await PayrollDistributionScheduleService.create(payload);
        toast.success("Tạo bảng phân bổ tiền lương thành công!");
      } else {
        await PayrollDistributionScheduleService.update(id, payload);
        toast.success("Cập nhật bảng phân bổ tiền lương thành công!");
      }
      navigation("/templates/payroll-distribution-schedule");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại!");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      headerInfo: {
        ...header,
        month: String(header.month || "").replace(/\D/g, "").padStart(2, "0").slice(0, 2),
      },
      rows: calculatedRows,
      totals,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "14px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "14px",
      fontFamily: "inherit",
      backgroundColor: "transparent",
      color: "#000",
    },
    table: { width: "100%", borderCollapse: "collapse", minWidth: 1700, fontSize: 12 },
    th: { border: "1px solid #000", padding: 4, textAlign: "center", fontWeight: "bold", verticalAlign: "middle" },
    td: { border: "1px solid #000", padding: 0, height: "30px" },
    inputText: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: 13,
      fontFamily: "inherit",
      background: "transparent",
      padding: "4px 6px",
      display: "block",
      color: "#000",
      resize: "none",
      lineHeight: 1.25,
    },
    inputNumber: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: 13,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} style={s.container}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ width: "45%" }}>
                    <div style={{ fontWeight: "bold", marginBottom: 5 }}>
                      Đơn vị:{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 230 }}
                        value={header.unit}
                        onChange={(e) => setHeaderField("unit", e.target.value)}
                      />
                    </div>
                    <div style={{ fontWeight: "bold" }}>
                      Bộ phận:{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 230 }}
                        value={header.department}
                        onChange={(e) => setHeaderField("department", e.target.value)}
                      />
                    </div>
                  </div>

                  <div style={{ textAlign: "center", width: "55%" }}>
                    <div style={{ fontWeight: "bold" }}>Mẫu số: 08 - LĐTL</div>
                    <div style={{ fontStyle: "italic", fontSize: "12px", marginTop: 4 }}>
                      (Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "center", margin: "10px 0 6px" }}>
                  <h2 style={{ fontSize: 29, fontWeight: "bold", margin: 0, textTransform: "uppercase" }}>
                    Bảng phân bổ tiền lương và các khoản trích theo lương
                  </h2>
                  <div style={{ marginTop: 4 }}>
                    Tháng{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 40, textAlign: "center" }}
                      value={header.month}
                      onChange={(e) => setHeaderField("month", e.target.value.replace(/\D/g, "").slice(0, 2))}
                      onBlur={() =>
                        setHeaderField("month", String(header.month || "").replace(/\D/g, "").padStart(2, "0").slice(0, 2))
                      }
                    />{" "}
                    năm{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 70, textAlign: "center" }}
                      value={header.year}
                      onChange={(e) => setHeaderField("year", e.target.value.replace(/\D/g, "").slice(0, 4))}
                    />
                  </div>
                </div>

                <div style={{ textAlign: "right", marginBottom: 8 }}>
                  Số:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 120 }}
                    value={header.documentNumber}
                    onChange={(e) => setHeaderField("documentNumber", e.target.value)}
                  />
                </div>

                <div style={{ marginTop: 8, overflowX: "auto", border: "1px solid #ccc" }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th} rowSpan={3}>Số TT</th>
                        <th style={s.th} rowSpan={3}>Ghi Có Tài khoản<br/>Đối tượng sử dụng (Ghi Nợ các Tài khoản)</th>
                        <th style={s.th} colSpan={3}>TK 334 - Phải trả người lao động</th>
                        <th style={s.th} colSpan={5}>TK 338 - Phải trả, phải nộp khác</th>
                        <th style={s.th} rowSpan={3}>TK 335<br/>Chi phí phải trả</th>
                        <th style={s.th} rowSpan={3}>Tổng cộng</th>
                        {!containView && <th style={s.th} rowSpan={3}>#</th>}
                      </tr>
                      <tr>
                        <th style={s.th} rowSpan={2}>Lương</th>
                        <th style={s.th} rowSpan={2}>Các khoản khác</th>
                        <th style={s.th} rowSpan={2}>Cộng Có TK334</th>
                        <th style={s.th} rowSpan={2}>Kinh phí công đoàn</th>
                        <th style={s.th} rowSpan={2}>Bảo hiểm xã hội</th>
                        <th style={s.th} rowSpan={2}>Bảo hiểm y tế</th>
                        <th style={s.th} rowSpan={2}>Bảo hiểm thất nghiệp</th>
                        <th style={s.th} rowSpan={2}>Cộng Có TK338 (3382, 3383, 3384, 3386)</th>
                      </tr>
                      <tr></tr>
                      <tr style={{ backgroundColor: "#fafafa" }}>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        <th style={s.th}>1</th>
                        <th style={s.th}>2</th>
                        <th style={s.th}>3</th>
                        <th style={s.th}>4</th>
                        <th style={s.th}>5</th>
                        <th style={s.th}>6</th>
                        <th style={s.th}>7</th>
                        <th style={s.th}>8</th>
                        <th style={s.th}>9</th>
                        <th style={s.th}>10</th>
                        {!containView && <th style={s.th}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {calculatedRows.map((row, index) => (
                        <tr key={`row-${index}`}>
                          <td style={{ ...s.td, textAlign: "center" }}>
                            <input
                              disabled={containView}
                              style={{ ...s.inputNumber, textAlign: "center", padding: 0 }}
                              value={row.serialNumber}
                              onChange={(e) => setRowField(index, "serialNumber", e.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <textarea
                              disabled={containView}
                              style={s.inputText}
                              rows={3}
                              value={row.debitObject}
                              onChange={(e) => setRowField(index, "debitObject", e.target.value)}
                            />
                          </td>
                          <td style={s.td}><input disabled={containView} style={s.inputNumber} inputMode="numeric" value={row.wage ? formatVndIntegerValue(row.wage) : ""} onChange={(e) => setRowField(index, "wage", e.target.value)} /></td>
                          <td style={s.td}><input disabled={containView} style={s.inputNumber} inputMode="numeric" value={row.otherAmount ? formatVndIntegerValue(row.otherAmount) : ""} onChange={(e) => setRowField(index, "otherAmount", e.target.value)} /></td>
                          <td style={{ ...s.td, textAlign: "right", paddingRight: 6 }}>{formatVndIntegerValue(row.tk334Total)}</td>
                          <td style={s.td}><input disabled={containView} style={s.inputNumber} inputMode="numeric" value={row.unionFee ? formatVndIntegerValue(row.unionFee) : ""} onChange={(e) => setRowField(index, "unionFee", e.target.value)} /></td>
                          <td style={s.td}><input disabled={containView} style={s.inputNumber} inputMode="numeric" value={row.socialInsurance ? formatVndIntegerValue(row.socialInsurance) : ""} onChange={(e) => setRowField(index, "socialInsurance", e.target.value)} /></td>
                          <td style={s.td}><input disabled={containView} style={s.inputNumber} inputMode="numeric" value={row.healthInsurance ? formatVndIntegerValue(row.healthInsurance) : ""} onChange={(e) => setRowField(index, "healthInsurance", e.target.value)} /></td>
                          <td style={s.td}><input disabled={containView} style={s.inputNumber} inputMode="numeric" value={row.unemploymentInsurance ? formatVndIntegerValue(row.unemploymentInsurance) : ""} onChange={(e) => setRowField(index, "unemploymentInsurance", e.target.value)} /></td>
                          <td style={{ ...s.td, textAlign: "right", paddingRight: 6 }}>{formatVndIntegerValue(row.tk338Total)}</td>
                          <td style={s.td}><input disabled={containView} style={s.inputNumber} inputMode="numeric" value={row.tk335Expense ? formatVndIntegerValue(row.tk335Expense) : ""} onChange={(e) => setRowField(index, "tk335Expense", e.target.value)} /></td>
                          <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>{formatVndIntegerValue(row.grandTotal)}</td>
                          {!containView && (
                            <td style={{ ...s.td, textAlign: "center" }}>
                              <button
                                type="button"
                                style={{ border: "none", background: "transparent", color: "red" }}
                                onClick={() => handleRemoveRow(index)}
                              >
                                X
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}

                      <tr>
                        <td style={{ ...s.td, textAlign: "center" }}></td>
                        <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>Cộng</td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>{formatVndIntegerValue(totals.wage)}</td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>{formatVndIntegerValue(totals.otherAmount)}</td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>{formatVndIntegerValue(totals.tk334Total)}</td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>{formatVndIntegerValue(totals.unionFee)}</td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>{formatVndIntegerValue(totals.socialInsurance)}</td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>{formatVndIntegerValue(totals.healthInsurance)}</td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>{formatVndIntegerValue(totals.unemploymentInsurance)}</td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>{formatVndIntegerValue(totals.tk338Total)}</td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>{formatVndIntegerValue(totals.tk335Expense)}</td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>{formatVndIntegerValue(totals.grandTotal)}</td>
                        {!containView && <td style={s.td}></td>}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <SoftBox mt={3} display="flex" justifyContent="space-between">
                  <SoftButton variant="gradient" color="light" onClick={() => navigation(-1)}>
                    Trở về
                  </SoftButton>

                  <SoftBox display="flex" gap={2}>
                    {!containView && (
                      <SoftButton variant="outlined" color="dark" onClick={handleAddRow}>
                        THÊM DÒNG
                      </SoftButton>
                    )}
                    {containView && (
                      <SoftButton variant="outlined" color="dark" onClick={handlePrint}>
                        In
                      </SoftButton>
                    )}
                    {!containView && (
                      <SoftButton variant="gradient" color="dark" onClick={handleSubmit} disabled={loadingSubmit}>
                        {id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
